import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { announcements } from "@/data/announcements";
import { attorneys } from "@/data/attorneys";
import LinkedText from "@/components/LinkedText";

const AUTO_ADVANCE_MS = 14000;

const AnnouncementsReel = () => {
  const items = useMemo(() => announcements, []);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      reducedMotion.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
  }, []);

  const count = items.length;
  const go = useCallback(
    (dir: number) => setIndex((i) => (i + dir + count) % count),
    [count]
  );
  const goTo = useCallback((i: number) => setIndex(i), []);

  useEffect(() => {
    if (count <= 1 || paused || reducedMotion.current) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), AUTO_ADVANCE_MS);
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [count, paused]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  if (count === 0) return null;

  const current = items[index];
  const attorney = current.attorneySlug
    ? attorneys.find((a) => a.slug === current.attorneySlug)
    : undefined;

  const mediaSrc = current.photoOverride || attorney?.photo || "/logo.png";
  const mediaIsLogo = !current.photos?.length && !current.photoOverride && !attorney?.photo;
  const linkTo = current.linkTo ?? (attorney ? `/team/${attorney.slug}` : undefined);
  const linkLabel = current.linkLabel ?? "View Attorney Bio";
  const caption = attorney?.name ?? current.caption;
  const captionSub = attorney?.title ?? current.captionSub;

  const MediaBlock = (
    <div className="relative aspect-[4/3] w-full overflow-hidden border border-primary/30 bg-secondary">
      {current.photos?.length ? (
        <div className="absolute inset-0 grid grid-cols-2 gap-px bg-primary/30">
          {current.photos.map((photo) => (
            <img
              key={photo.src}
              src={photo.src}
              alt={photo.alt}
              className="h-full w-full min-w-0 object-cover object-top"
            />
          ))}
        </div>
      ) : mediaIsLogo ? (
        <div className="absolute inset-0 flex items-center justify-center p-10">
          <img src={mediaSrc} alt="Vaughan McLean" className="max-h-full max-w-full object-contain" />
        </div>
      ) : (
        <img
          src={mediaSrc}
          alt={attorney?.name || "Firm announcement"}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      )}
      {caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 via-background/70 to-transparent px-6 py-4">
          <p className="font-heading text-xl text-foreground">{caption}</p>
          {captionSub && (
            <p className="text-[10px] tracking-[0.2em] uppercase font-body text-primary">
              {captionSub}
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <section
      className="section-dark border-t border-border py-20"
      aria-label="Firm headlines"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="container mx-auto px-8 lg:px-16">
        <div className="mb-10 flex items-end justify-between gap-6">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-body text-primary mb-3">
              Latest News
            </p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground">
              Firm Headlines
            </h2>
          </div>
          {count > 1 && (
            <div className="hidden sm:flex items-center gap-2">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Previous headline"
                className="p-2 border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Next headline"
                className="p-2 border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="relative" aria-live="polite">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
            >
              <div className="order-2 lg:order-1">
                <h3 className="font-heading text-2xl md:text-3xl text-foreground mb-5 leading-snug">
                  {current.headline}
                </h3>
                <div className="space-y-4">
                  {(Array.isArray(current.body) ? current.body : [current.body]).map((paragraph, i) => (
                    <p
                      key={`${current.id}-p-${i}`}
                      className="text-muted-foreground font-body leading-relaxed text-sm md:text-base"
                    >
                      <LinkedText text={paragraph} />
                    </p>
                  ))}
                </div>
                {linkTo && (
                  <Link
                    to={linkTo}
                    className="mt-6 inline-block text-xs tracking-[0.2em] uppercase font-body text-primary border-b border-primary pb-1 hover:text-foreground hover:border-foreground transition-colors"
                  >
                    {linkLabel}
                  </Link>
                )}
              </div>
              <div className="order-1 lg:order-2">
                {linkTo ? (
                  <Link to={linkTo} aria-label={current.linkLabel ?? `View ${attorney?.name}'s bio`}>
                    {MediaBlock}
                  </Link>
                ) : (
                  MediaBlock
                )}
              </div>
            </motion.div>
          </AnimatePresence>

          {count > 1 && (
            <div className="mt-8">
              <div
                className="mx-auto h-0.5 w-full max-w-md overflow-hidden bg-border"
                aria-hidden="true"
              >
                <div
                  key={current.id}
                  className="h-full w-full origin-left bg-primary"
                  style={{
                    animationName: "headline-progress",
                    animationDuration: `${AUTO_ADVANCE_MS}ms`,
                    animationTimingFunction: "linear",
                    animationFillMode: "forwards",
                    animationPlayState: paused ? "paused" : "running",
                  }}
                />
              </div>
              <div className="mt-6 flex items-center justify-center gap-2">
                {items.map((it, i) => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => goTo(i)}
                    aria-label={`Go to headline ${i + 1}`}
                    aria-current={i === index}
                    className={`h-1.5 transition-all ${
                      i === index ? "w-8 bg-primary" : "w-4 bg-border hover:bg-muted-foreground"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AnnouncementsReel;
